import React, { useCallback, useMemo, useReducer, useState } from 'react';
import BritgeForm from './BritgeForm';
import ModalFormContext from '../../store/ModalFormContext';

const mapFormIndex = (forms) => {
  return (forms || []).map((form, index) => ({ ...form, index }));
}

const formReducer = (state, data) => {
  switch (data.type) {
    case "SET_FORM":
      return mapFormIndex(data.forms);
    case "APPEND_FORM":
      return mapFormIndex([...(state || []), ...(data.forms || [])]);
    case "REPLACE_FORM": {
      const returnForms = [
        ...(state || []).slice(0, data.index),
        ...(data.forms || []),
        ...(state || []).slice(data.index + 1),
      ];
      return mapFormIndex(returnForms);
    }
    case "RESET_FORM":
      return [];
    default:
      throw new Error(`Unhandled action type: ${data.type}`);
  }
};

const BritgeComponentFormModal = ({
  url,
  text,
  navigateUrl,
  initialData,
  buttonClassName = 'btn btn-primary',
  modalClassName = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [modalForm, setModalFormState] = useState(null);
  const [currentForms, dispatchForms] = useReducer(formReducer, []);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => {
    setModalFormState(null);
    setIsModalOpen(false);
    dispatchForms({ type: 'RESET_FORM' });
  }, []);

  const setModalForm = useCallback((next) => {
    if (typeof next === 'function') {
      setModalFormState((prev) => next(prev || {}));
      return;
    }
    setModalFormState(next);
  }, []);

  const onAfterSave = useCallback(() => {
    if (!navigateUrl) return;
    window.location.href = navigateUrl;
  }, [navigateUrl]);

  const onOpen = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) return;
    openModal();
    setModalFormState({
      url,
      initialData,
      callbackFunc: onAfterSave,
    });
  }, [url, initialData, openModal, onAfterSave]);

  const refreshForm = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 100);
  }, []);

  const saveHook = useCallback((data) => {
    if (!data?.success) return;
    closeModal();
    onAfterSave();
  }, [closeModal, onAfterSave]);

  const modalFormContext = useMemo(() => ({
    isModalOpen,
    currentForms,
    dispatchForms,
    forms: [],
    ...modalForm,
    openModal,
    closeModal,
    setModalForm,
  }), [isModalOpen, currentForms, modalForm, openModal, closeModal, setModalForm]);

  let classes = `custom-modal form-modal d-flex flex-column ${modalClassName || ''}`.trim();
  if (isModalOpen) {
    classes += ' active';
  }

  return (
    <ModalFormContext.Provider value={modalFormContext}>
      {isModalOpen ? <div className="custom-modal-backdrop" onClick={closeModal} /> : null}
      <div className={`${classes} bg-white`}>
        <div className="bg-white border-bottom position-sticky top-0 form-modal custom-modal-content" style={{ zIndex: "1" }}>
          <div className="custom-modal-close px-2">
            <div className="btn px-1" onClick={closeModal}><i className="bi bi-x-octagon" /></div>
            {modalForm?.url ? (
              <div className="btn px-1" onClick={refreshForm}><i className="bi bi-arrow-clockwise" /></div>
            ) : null}
          </div>
          <h4 className="text-center mb-1">{modalForm?.form_title || ''}</h4>
        </div>
        <div id="britge-component-form-modal-content" className="bg-white form-modal-content">
          {(isModalOpen && modalForm?.url && !isRefreshing)
            ? <BritgeForm url={modalForm.url} saveHook={saveHook} />
            : null}
        </div>
      </div>

      <button type="button" className={buttonClassName} onClick={onOpen}>
        {text || 'Open form'}
      </button>
    </ModalFormContext.Provider>
  );
};

export default BritgeComponentFormModal;

